import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/returns.scss";

function Returns() {
  const returnWindow = siteConfig.policies?.returnWindow || 30;
  const returnWindowLabel = /day/i.test(String(returnWindow))
    ? String(returnWindow)
    : `${returnWindow} days`;
  const lastUpdated = siteConfig.policies?.lastUpdated || "August 14, 2026";

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="returns policy-page">
      <header className="policy-page__hero">
        <p className="policy-page__kicker">Customer care</p>
        <h1>Returns &amp; exchanges</h1>
        <p>
          We want your {siteConfig.brandName} ritual to feel right. Here is what
          to know if a purchase does not work for you.
        </p>
        <span>Last updated {lastUpdated}</span>
      </header>

      <section className="policy-summary" aria-label="Returns at a glance">
        <article>
          <strong>{returnWindowLabel}</strong>
          <span>to request an eligible return</span>
        </article>
        <article>
          <strong>Original condition</strong>
          <span>unused, unopened and safely packed</span>
        </article>
        <article>
          <strong>Proof of purchase</strong>
          <span>order number or store receipt required</span>
        </article>
      </section>

      <div className="policy-page__layout">
        <nav className="policy-page__nav" aria-label="Returns policy sections">
          <span>On this page</span>
          <a href="#returns-eligible">Eligibility</a>
          <a href="#returns-start">Start a return</a>
          <a href="#returns-refunds">Refunds</a>
          <a href="#returns-damaged">Order issues</a>
          <a href="#returns-contact">Contact</a>
        </nav>

        <article className="policy-page__content">
          <section className="policy-page__lead" aria-label="Returns overview">
            <p>
              Contact us within {returnWindowLabel} of delivery before sending
              anything back. Returns sent without authorization may be delayed or
              refused. This policy applies to purchases made on this website;
              products bought elsewhere must be returned to that retailer.
            </p>
          </section>

          <section id="returns-eligible">
            <p className="policy-page__number">01</p>
            <h2>What is eligible</h2>
            <p>
              Products must be unused, unopened, in their original packaging and
              accompanied by proof of purchase. Sets and gifts must be returned
              with all components. We inspect each item when it arrives.
            </p>
            <p>The following are not eligible unless faulty or required by law:</p>
            <ul>
              <li>Opened or used personal-care products for hygiene reasons.</li>
              <li>Final-sale, promotional gift and digital gift-card items.</li>
              <li>Products damaged after delivery or missing original parts.</li>
              <li>Items purchased from an unauthorized seller.</li>
            </ul>
          </section>

          <section id="returns-start">
            <p className="policy-page__number">02</p>
            <h2>How to start a return</h2>
            <ol className="policy-steps">
              <li>
                <span>1</span>
                <div>
                  <strong>Contact customer care</strong>
                  <p>
                    Email <a href={`mailto:${siteConfig.contact.email}`}>
                      {siteConfig.contact.email}
                    </a>{" "}
                    with your order number and the item you wish to return.
                  </p>
                </div>
              </li>
              <li>
                <span>2</span>
                <div>
                  <strong>Wait for instructions</strong>
                  <p>
                    We will confirm eligibility and provide the correct return
                    address. A public business or returns address is not currently
                    listed on this website, so please do not send an item before
                    receiving those instructions.
                  </p>
                </div>
              </li>
              <li>
                <span>3</span>
                <div>
                  <strong>Pack and send securely</strong>
                  <p>
                    Use protective packaging and a tracked service. Keep the
                    receipt until your refund is complete.
                  </p>
                </div>
              </li>
            </ol>
          </section>

          <section id="returns-refunds">
            <p className="policy-page__number">03</p>
            <h2>Refunds and exchanges</h2>
            <p>
              Once inspected, we will notify you whether the return is approved.
              Approved refunds are issued to the original payment method,
              normally within 5–10 business days. Your bank may need additional
              time to post the credit. Original delivery charges, gift wrapping
              and return postage are not refunded unless the item was faulty or
              we sent the wrong product.
            </p>
            <p>
              We do not offer direct product exchanges online. For the fastest
              result, return the eligible item and place a new order separately.
              Promotional pricing applies only while the offer remains active.
            </p>
          </section>

          <section id="returns-damaged">
            <p className="policy-page__number">04</p>
            <h2>Damaged, missing or incorrect items</h2>
            <p>
              Please inspect your parcel on arrival and contact us within 7 days
              if an item is damaged, missing or incorrect. Include your order
              number and clear photos of the item, shipping label and packaging.
              Keep all packaging until we resolve the issue. We will arrange a
              replacement or refund where the claim is verified.
            </p>
          </section>

          <section>
            <p className="policy-page__number">05</p>
            <h2>International returns and gifts</h2>
            <p>
              International customers are responsible for tracked return
              transport and any non-refundable duties unless the product was
              faulty. Mark the parcel as returned merchandise where permitted.
              Gift recipients may receive store credit after verification;
              refunds to a payment method can only go to the original purchaser.
            </p>
          </section>

          <section id="returns-contact">
            <p className="policy-page__number">06</p>
            <h2>Need help?</h2>
            <p>
              Visit our <Link to="/contact">contact page</Link>, email{" "}
              <a href={`mailto:${siteConfig.contact.email}`}>
                {siteConfig.contact.email}
              </a>{" "}
              during {siteConfig.contact.hours}.
            </p>
          </section>

          <div className="policy-page__related">
            <span>Related policies</span>
            <Link to="/shipping" onClick={toTop}>Shipping</Link>
            <Link to="/privacy" onClick={toTop}>Privacy</Link>
            <Link to="/terms" onClick={toTop}>Terms &amp; conditions</Link>
          </div>
        </article>
      </div>
    </main>
  );
}

export default Returns;
